'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQSearchProps {
  onSearch: (query: string) => void;
  totalResults: number;
}

const FAQSearch = ({ onSearch, totalResults }: FAQSearchProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const popularSearches = [
    "AI implementation timeline",
    "Pricing and packages",
    "ROI expectations",
    "Technical requirements",
    "Data security",
    "Integration process"
  ];

  const handleSearchChange = (value: string) => {
    if (!isHydrated) return;
    
    setSearchQuery(value);
    onSearch(value);

    if (value.length > 2) {
      const filtered = popularSearches.filter(s => 
        s.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!isHydrated) return;
    
    setSearchQuery(suggestion);
    onSearch(suggestion);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    if (!isHydrated) return;
    
    setSearchQuery('');
    onSearch('');
    setShowSuggestions(false);
  };

  if (!isHydrated) {
    return (
      <div className="glassmorphic rounded-2xl p-8 mb-8">
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <Icon name="MagnifyingGlassIcon" size={24} className="text-muted-foreground" />
          </div>
          <input
            type="text"
            placeholder="Zoek in veelgestelde vragen..."
            className="w-full pl-14 pr-14 py-4 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300"
            disabled
          />
        </div>
      </div>
    );
  }

  return (
    <div className="glassmorphic rounded-2xl p-8 mb-8">
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="MagnifyingGlassIcon" size={24} className="text-muted-foreground" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Zoek in veelgestelde vragen..."
          className="w-full pl-14 pr-14 py-4 bg-input border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300"
        />
        {searchQuery && (
          <button
            onClick={handleClearSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-lg transition-colors duration-300"
            aria-label="Clear search"
          >
            <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
          </button>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-elevated overflow-hidden z-10">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors duration-300 flex items-center space-x-3"
              >
                <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {searchQuery && (
        <div className="mt-4 text-sm text-muted-foreground">
          {totalResults} {totalResults === 1 ? 'resultaat' : 'resultaten'} gevonden
        </div>
      )}

      <div className="mt-6">
        <p className="text-sm text-muted-foreground mb-3">Populaire zoekopdrachten:</p>
        <div className="flex flex-wrap gap-2">
          {popularSearches.map((search, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(search)}
              className="px-4 py-2 bg-muted hover:bg-primary/20 text-foreground text-sm rounded-lg transition-all duration-300 hover:glow-primary"
            >
              {search}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQSearch;